class AdvancedStockStatusManager {
  constructor() {
    this.inventoryData = null;
    this.init();
  }

  init() {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    this.loadInventoryData();
    if (!this.inventoryData) return; // kein #product-inventory-data im DOM → NOOP
    this.cartQtyCache = null;
    this.setupEventListeners();
    this.updateStockStatus();
    this._evaluateInitialBackorderSpan();
  }

  loadInventoryData() {
    const dataScript = document.getElementById('product-inventory-data');
    if (!dataScript) return;
    try {
      const raw = JSON.parse(dataScript.textContent);
      raw.variants = (raw.variants ?? []).map(v => {
        if (!v.enc) return v;
        try {
          const [management, policy, qty] = atob(v.enc).split('|');
          return {
            id:                   v.id,
            available:            v.available,
            title:                v.title,
            inventory_management: management,
            inventory_policy:     policy,
            inventory_quantity:   parseInt(qty, 10),
          };
        } catch {
          return v;
        }
      });
      this.inventoryData = raw;
    } catch {
      // Bewusst kein Log
    }
  }

  setupEventListeners() {
    if (
      typeof subscribe === "function" &&
      typeof PUB_SUB_EVENTS !== "undefined"
    ) {
      subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, () => {
        setTimeout(() => this.updateStockStatus(), 100);
      });
    }

    document
      .querySelectorAll("variant-selects input, variant-selects select")
      .forEach((el) =>
        el.addEventListener("change", () => {
          setTimeout(() => this.updateStockStatus(), 150);
        })
      );

    const hiddenInput = document.querySelector('input[name="id"]');
    if (hiddenInput) {
      new MutationObserver(() => this.updateStockStatus()).observe(
        hiddenInput,
        { attributes: true, attributeFilter: ["value"] }
      );
    }

    // Invalidate cart cache after every cart change so the next
    // updateStockStatus() call re-fetches current quantities.
    document.addEventListener('backorder:cart-updated', () => {
      this.cartQtyCache = null;
      this.updateStockStatus();
    });
  }

  async updateStockStatus() {
    const container = document.getElementById("stock-status");
    if (!container || !this.inventoryData) return;

    const currentVariantId = this.getCurrentVariantId();
    if (!currentVariantId) return;

    const variant = this.inventoryData.variants.find(
      (v) => v.id == currentVariantId
    );
    if (!variant) return;

    // Fetch cart on first call so partial-backorder is visible on PDP load/reload.
    // Subsequent calls (variant switches) reuse the cache; cache is invalidated
    // by backorder:cart-updated (fired by quick-add-stock.js after every cart change).
    if (this.cartQtyCache === null) {
      this.cartQtyCache = await this._fetchCartQtyMap();
    }

    const cartQty = this.cartQtyCache.get(currentVariantId) ?? 0;
    this.renderStockStatus(container, variant, cartQty);
  }

  async _fetchCartQtyMap() {
    try {
      const res = await fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } });
      if (!res.ok) return new Map();
      const { items } = await res.json();
      const map = new Map();
      for (const item of items ?? []) {
        map.set(item.variant_id, (map.get(item.variant_id) ?? 0) + item.quantity);
      }
      return map;
    } catch {
      return new Map();
    }
  }

  getCurrentVariantId() {
    const methods = [
      () => document.querySelector('input[name="id"]')?.value,
      () =>
        document.querySelector("[data-selected-variant-id]")?.dataset
          ?.selectedVariantId,
      () => new URLSearchParams(window.location.search).get("variant"),
    ];

    for (const method of methods) {
      const result = method();
      if (result) return parseInt(result);
    }
    return null;
  }

  renderStockStatus(container, variant, cartQty = 0) {
    const status = this.determineStockStatus(variant);

    container.innerHTML = `
    <span class="stock-icon">${status.icon}</span>
    <span class="stock-text">${status.text}</span>
    ${
      status.showCount
        ? `<span class="stock-count">(${variant.inventory_quantity})</span>`
        : ""
    }
    ${
      status.urgency
        ? `<span class="stock-urgency">${status.urgency}</span>`
        : ""
    }
  `;

    container.className = `product__stock-status ${status.class}`;
    this.addEnhancedFeatures(container, variant, status);

    // Re-inject .backorder-qty span after innerHTML reset.
    // renderStockStatus() wipes the DOM on every call, so the span must be
    // rebuilt here. cartQty comes from _fetchCartQtyMap() so both full- and
    // partial-backorder are correctly shown on PDP load and reload.
    this._reInjectBackorderSpan(container, variant, cartQty);
  }

  _reInjectBackorderSpan(container, variant, cartQty = 0) {
    if (
      variant.inventory_management !== 'shopify' ||
      variant.inventory_policy     !== 'continue'
    ) return;

    const invQty = variant.inventory_quantity ?? 0;

    // Zwei-Fall-System identisch zu backorder-quantity-watch.js
    const isFullBackorder    = invQty <= 0 && cartQty >= 1;
    const isPartialBackorder = invQty > 0  && cartQty > invQty;

    let boQty = 0;
    if (isFullBackorder)    boQty = cartQty;
    if (isPartialBackorder) boQty = cartQty - invQty;

    // Nur anzeigen wenn der Kunde tatsächlich Artikel im Cart hat
    const showSpan = (isFullBackorder || isPartialBackorder) && boQty > 0;
    if (!showSpan) return;

    const el   = document.getElementById('backorder-i18n');
    const i18n = el
      ? (() => { try { return JSON.parse(el.textContent); } catch { return {}; } })()
      : {};
    const key  = boQty === 1 ? 'inline_one' : 'inline_other';
    const tpl  = i18n[key] ?? i18n['backorder_qty_one'] ?? i18n['backorder_qty_other'];
    const displayQty = boQty > 0 ? boQty : 1;
    const text = tpl ? tpl.replace('__COUNT__', String(displayQty)) : `${displayQty} Stück auf Nachlieferung`;

    const span = document.createElement('span');
    span.className   = 'backorder-qty';
    span.textContent = text;
    container.appendChild(span);
  }

  determineStockStatus(variant) {
    const hasTracking     = variant.inventory_management === "shopify";
    const allowsBackorder = variant.inventory_policy === "continue";
    const qty             = variant.inventory_quantity || 0;
    const isAvailable     = variant.available;

    if (!hasTracking) {
      return {
        status: "available",
        text: "Verfügbar",
        class: "stock-status--available",
        icon: "✅",
        showCount: false,
      };
    }

    if (qty <= 0) {
      if (allowsBackorder && isAvailable) {
        return {
          status: "backorder",
          text: "Vorbestellung möglich",
          class: "stock-status--backorder",
          icon: "⚠️",
          showCount: false,
          urgency: "Lieferung erfolgt autom. sobald wieder auf Lager",
        };
      }
      return {
        status: "out-of-stock",
        text: "Ausverkauft",
        class: "stock-status--out-of-stock",
        icon: "❌",
        showCount: false,
      };
    }

    if (qty <= 3) {
      return {
        status: "low-stock",
        text: "Wenige verfügbar",
        class: "stock-status--low-stock",
        icon: "🔥",
        showCount: true,
        urgency: "Schnell zugreifen!",
      };
    }

    if (qty <= 10) {
      return {
        status: "medium-stock",
        text: "Bald ausverkauft",
        class: "stock-status--medium-stock",
        icon: "⚡",
        showCount: true,
      };
    }

    return {
      status: "in-stock",
      text: "Auf Lager",
      class: "stock-status--in-stock",
      icon: "✅",
      showCount: true,
    };
  }

  addEnhancedFeatures(container, variant, status) {
    if (status.status === "out-of-stock") {
      this.addRestockNotification(container, variant);
    }

    if (status.status === "medium-stock") {
      this.addInventoryTrend(container, variant.inventory_quantity);
    }
  }

  addRestockNotification(container, variant) {
    // Stub – eigene Wiederauffüllungs-Logik hier einfügen (z. B. Klaviyo).
  }

  addInventoryTrend(container, quantity) {
    const trendDiv = document.createElement("div");
    trendDiv.className = "inventory-trend";

    const percentage = Math.max(0, Math.min(100, (quantity / 50) * 100));

    trendDiv.innerHTML = `
      <div class="trend-info">
        <span class="trend-label">📊 Verfügbarkeit:</span>
        <div class="trend-bar">
          <div class="trend-fill" style="width: ${percentage}%"></div>
        </div>
        <span class="trend-text">${quantity} von ca. 50 verfügbar</span>
      </div>
    `;

    container.appendChild(trendDiv);
  }

  handleRestockSignup(form, variantId) {
    const email  = form.querySelector('input[type="email"]').value;
    const button = form.querySelector("button");

    button.textContent = "Wird verarbeitet...";
    button.disabled    = true;

    setTimeout(() => {
      console.log(`Restock-Anmeldung: ${email} für Variante ${variantId}`);

      form.innerHTML = `
        <div class="success-message">
          <span class="success-icon">✅</span>
          <span class="success-text">Benachrichtigung eingerichtet!</span>
          <small>Sie erhalten eine E-Mail, sobald das Produkt wieder verfügbar ist.</small>
        </div>
      `;
    }, 2000);
  }

  // Injects the .backorder-qty span on initial PDP load for full-backorder variants.
  // Reads this.inventoryData (already parsed in loadInventoryData) — no second JSON.parse.
  // Cart quantity is unknown at load time → only full-backorder (invQty <= 0) is shown.
  // Partial-backorder is handled after the user adds items via backorder:status-updated.
  _evaluateInitialBackorderSpan() {
    const container = document.getElementById('stock-status');
    if (!container) return;

    const variantId = this.getCurrentVariantId();
    if (!variantId) return;

    const variant = (this.inventoryData?.variants ?? []).find(v => v.id === variantId);
    if (!variant) return;

    if (
      variant.inventory_management !== 'shopify' ||
      variant.inventory_policy     !== 'continue'
    ) return;

    if ((variant.inventory_quantity ?? 0) <= 0) {
      // Full-backorder: dispatch status-updated so the IIFE's updatePdpSpan runs.
      // boQty=1 symbolisch — Cart-Menge beim Load unbekannt.
      document.dispatchEvent(new CustomEvent('backorder:status-updated', {
        bubbles: false,
        detail: { bo: true, boQty: 1, variantId },
      }));
    }
  }
}

// Manager initialisieren
new AdvancedStockStatusManager();

// ── backorder:status-updated → .backorder-qty Span (PDP + Cart) ───────────────
(function initBackorderStatusListener() {
  if (!document.getElementById('stock-status')) return;

  /**
   * Lokalisierter Backorder-Stückzahl-Text.
   * Liest aus #backorder-i18n (inline_one/inline_other).
   * Fallback auf generischen deutschen Text.
   */
  function getBackorderQtyText(boQty) {
    const el   = document.getElementById('backorder-i18n');
    const i18n = el
      ? (() => { try { return JSON.parse(el.textContent); } catch { return {}; } })()
      : {};
    const key = boQty === 1 ? 'inline_one' : 'inline_other';
    const tpl = i18n[key] ?? i18n['backorder_qty_one'] ?? i18n['backorder_qty_other'];
    if (tpl) return tpl.replace('__COUNT__', String(boQty));
    return `${boQty} Stück auf Nachlieferung`;
  }

  /**
   * Aktive Varianten-ID auf der PDP.
   */
  function getActiveVariantId() {
    const fromInput = document.querySelector('input[name="id"]')?.value;
    if (fromInput) return parseInt(fromInput, 10);
    const fromData  = document.querySelector('[data-selected-variant-id]')?.dataset?.selectedVariantId;
    if (fromData)  return parseInt(fromData, 10);
    const fromUrl   = new URLSearchParams(window.location.search).get('variant');
    if (fromUrl)   return parseInt(fromUrl, 10);
    return null;
  }

  /**
   * Aktualisiert .backorder-qty Span im #stock-status Container.
   *
   * bo=true  → Span einfügen / Text setzen
   * bo=false → Span entfernen
   */
  function updatePdpSpan(bo, boQty) {
    const pdpContainer = document.getElementById('stock-status');
    if (!pdpContainer) return;

    let span = pdpContainer.querySelector('.backorder-qty');
    if (!bo) {
      span?.remove();
      return;
    }
    if (!span) {
      span = document.createElement('span');
      span.className = 'backorder-qty';
      pdpContainer.appendChild(span);
    }
    span.textContent = getBackorderQtyText(boQty);
  }

  /**
   * Aktualisiert alle .backorder-qty Spans im Cart für eine Variante.
   */
  function updateCartBackorderSpans(variantId, bo, boQty) {
    document
      .querySelectorAll(`[data-cart-backorder-variant="${variantId}"]`)
      .forEach((notice) => {
        let span = notice.querySelector('.backorder-qty');
        if (!span) {
          span = document.createElement('span');
          span.className = 'backorder-qty';
          notice.appendChild(span);
        }
        if (bo && boQty > 0) {
          span.textContent   = getBackorderQtyText(boQty);
          span.style.display = '';
        } else {
          span.textContent   = '';
          span.style.display = 'none';
        }
      });
  }

  // ── Event-Listener: backorder:status-updated (Mengenänderung) ────────────
  document.addEventListener('backorder:status-updated', (event) => {
    const { bo, boQty, variantId } = event.detail ?? {};

    // PDP: Varianten-Guard
    const pdpContainer = document.getElementById('stock-status');
    if (pdpContainer) {
      const activeId = getActiveVariantId();
      if (activeId === null || activeId === variantId) {
        updatePdpSpan(bo, boQty);
      }
    }

    // Cart: alle passenden Notices aktualisieren
    updateCartBackorderSpans(variantId, bo, boQty);
  });

  // ── Cart-Refresh: Visibility-Pass nach js-contents-Swap ──────────────────
  document.addEventListener('cart:updated', () => {
    document
      .querySelectorAll('[data-cart-backorder-variant] .backorder-qty')
      .forEach((span) => {
        if (span.textContent.trim()) {
          span.style.display = '';
        }
      });
  });

})();
